import { useState, useMemo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Aperture, Focus, Camera, Ruler, Eye,
  Target, RotateCcw, Infinity as InfinityIcon
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

// Sensor sizes with circle of confusion
const SENSOR_SIZES = {
  'full-frame': { width: 36, height: 24, coc: 0.030, name: 'Full Frame' },
  'aps-c-canon': { width: 22.3, height: 14.9, coc: 0.019, name: 'APS-C Canon' },
  'aps-c': { width: 23.5, height: 15.6, coc: 0.020, name: 'APS-C Nikon/Sony' },
  'micro43': { width: 17.3, height: 13, coc: 0.015, name: 'Micro 4/3' },
  'medium-format': { width: 43.8, height: 32.9, coc: 0.043, name: 'Medium Format' },
  '1inch': { width: 13.2, height: 8.8, coc: 0.011, name: '1 inch' },
};

// Translation keys for the sensor size button labels (first word of the translated name is shown)
const SENSOR_NAME_KEYS = {
  'full-frame': 'dof.sensorFullFrame',
  'aps-c-canon': 'dof.sensorApsCCanon',
  'aps-c': 'dof.sensorApsC',
  'micro43': 'dof.sensorMicro43',
  'medium-format': 'dof.sensorMediumFormat',
  '1inch': 'dof.sensor1Inch',
};

// Common apertures
const APERTURES = [1.4, 1.8, 2, 2.8, 4, 5.6, 8, 11, 16, 22];

const DOFCalculator = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [focalLength, setFocalLength] = useState(50); // mm
  const [aperture, setAperture] = useState(5.6);
  const [focusDistance, setFocusDistance] = useState(5); // meters
  const [sensorSize, setSensorSize] = useState('full-frame');

  const sensor = SENSOR_SIZES[sensorSize];
  const coc = sensor.coc; // Circle of confusion in mm

  // Calculate DOF values
  const calculations = useMemo(() => {
    const f = focalLength; // focal length in mm
    const N = aperture; // f-number
    const s = focusDistance * 1000; // focus distance in mm
    const c = coc; // circle of confusion in mm

    // Hyperfocal distance (mm)
    const H = (f * f) / (N * c) + f;
    const hyperfocal = H / 1000; // convert to meters

    // Near limit (mm)
    const Dn = (s * (H - f)) / (H + s - 2 * f);
    const nearLimit = Math.max(0, Dn / 1000); // convert to meters

    // Far limit (mm)
    let farLimit;
    if (s >= H - f) {
      farLimit = Infinity;
    } else {
      const Df = (s * (H - f)) / (H - s);
      farLimit = Df / 1000; // convert to meters
    }

    // Total DOF
    const dof = farLimit === Infinity ? Infinity : farLimit - nearLimit;

    // DOF in front and behind focus
    const dofFront = focusDistance - nearLimit;
    const dofBehind = farLimit === Infinity ? Infinity : farLimit - focusDistance;

    return {
      hyperfocal,
      nearLimit,
      farLimit,
      dof,
      dofFront,
      dofBehind
    };
  }, [focalLength, aperture, focusDistance, coc]);

  // Draw DOF visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Scale for visualization (log scale for better representation)
    const maxDist = Math.min(calculations.farLimit, 100);
    const scaleX = (dist) => {
      if (dist === Infinity) return width - 20;
      return 50 + Math.log10(Math.max(0.1, dist) + 1) / Math.log10(maxDist + 1) * (width - 100);
    };

    // Ground plane
    const groundY = height * 0.7;
    ctx.fillStyle = '#2a3a2a';
    ctx.fillRect(0, groundY, width, height - groundY);

    // Distance markers
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';

    const distances = [0.5, 1, 2, 5, 10, 20, 50, 100];
    distances.forEach(d => {
      if (d <= maxDist) {
        const x = scaleX(d);
        ctx.beginPath();
        ctx.moveTo(x, groundY - 5);
        ctx.lineTo(x, groundY + 5);
        ctx.stroke();
        ctx.fillText(`${d}m`, x, groundY + 18);
      }
    });

    // Camera position
    const camX = 30;
    const camY = groundY - 30;
    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(camX, camY, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'white';
    ctx.font = '12px sans-serif';
    ctx.fillText('📷', camX - 6, camY + 4);

    // DOF zone
    const nearX = scaleX(calculations.nearLimit);
    const farX = scaleX(Math.min(calculations.farLimit, maxDist));
    const focusX = scaleX(focusDistance);

    // Out of focus zone (near)
    ctx.fillStyle = 'rgba(239, 68, 68, 0.2)';
    ctx.fillRect(camX + 15, groundY - 60, nearX - camX - 15, 60);

    // In focus zone (DOF)
    ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
    ctx.fillRect(nearX, groundY - 60, farX - nearX, 60);

    // Out of focus zone (far) if not infinity
    if (calculations.farLimit < maxDist) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.2)';
      ctx.fillRect(farX, groundY - 60, width - farX - 20, 60);
    }

    // Near limit line
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(nearX, groundY - 70);
    ctx.lineTo(nearX, groundY);
    ctx.stroke();

    // Far limit line
    ctx.strokeStyle = calculations.farLimit === Infinity ? '#22c55e' : '#ef4444';
    ctx.beginPath();
    ctx.moveTo(farX, groundY - 70);
    ctx.lineTo(farX, groundY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Focus point
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(focusX, groundY - 80);
    ctx.lineTo(focusX, groundY);
    ctx.stroke();

    // Focus point marker
    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.arc(focusX, groundY - 85, 6, 0, Math.PI * 2);
    ctx.fill();

    // Labels
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';

    ctx.fillStyle = '#ef4444';
    ctx.fillText(`${t('dof.near')}: ${calculations.nearLimit.toFixed(2)}m`, nearX, 20);

    ctx.fillStyle = calculations.farLimit === Infinity ? '#22c55e' : '#ef4444';
    ctx.fillText(calculations.farLimit === Infinity ? `${t('dof.far')}: ∞` : `${t('dof.far')}: ${calculations.farLimit.toFixed(2)}m`, farX, 20);

    ctx.fillStyle = '#f59e0b';
    ctx.fillText(`${t('dof.focus')}: ${focusDistance}m`, focusX, groundY - 95);

    // DOF span label
    ctx.fillStyle = '#22c55e';
    ctx.fillText(
      calculations.dof === Infinity ? `${t('dof.dofLabel')}: ∞` : `${t('dof.dofLabel')}: ${calculations.dof.toFixed(2)}m`,
      (nearX + farX) / 2,
      groundY - 35
    );

    // Hyperfocal marker if visible
    if (calculations.hyperfocal <= maxDist) {
      const hfX = scaleX(calculations.hyperfocal);
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(hfX, groundY - 70);
      ctx.lineTo(hfX, groundY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#8b5cf6';
      ctx.fillText(`${t('dof.hf')}: ${calculations.hyperfocal.toFixed(1)}m`, hfX, groundY + 35);
    }

  }, [calculations, focusDistance, t]);

  const formatDistance = (d) => {
    if (d === Infinity) return '∞';
    if (d < 1) return `${(d * 100).toFixed(0)}cm`;
    return `${d.toFixed(2)}m`;
  };

  return (
    <div className="space-y-4">
      {/* DOF Visualization */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Aperture className="w-4 h-4 text-cyan-400" />
              {t('dof.title')}
            </CardTitle>
            <Badge className="bg-green-500/20 text-green-300">
              {t('dof.dofLabel')}: {formatDistance(calculations.dof)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Canvas */}
          <div className="relative rounded-lg overflow-hidden border border-white/10">
            <canvas
              ref={canvasRef}
              width={400}
              height={180}
              className="w-full"
            />
          </div>

          {/* Sensor Size */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">{t('dof.sensorSize')}</Label>
            <div className="flex flex-wrap gap-1">
              {Object.entries(SENSOR_SIZES).map(([key, value]) => (
                <Button
                  key={key}
                  variant={sensorSize === key ? 'default' : 'ghost'}
                  size="sm"
                  className={`h-7 px-2 text-xs ${sensorSize === key ? 'bg-cyan-500' : ''}`}
                  onClick={() => setSensorSize(key)}
                >
                  {t(SENSOR_NAME_KEYS[key]).split(' ')[0]}
                </Button>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Camera className="w-3 h-3" />
                {t('dof.focal')}: {focalLength}mm
              </Label>
              <Slider
                value={[focalLength]}
                onValueChange={([v]) => setFocalLength(v)}
                min={8}
                max={400}
                step={1}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Aperture className="w-3 h-3" />
                f/{aperture}
              </Label>
              <Slider
                value={[aperture]}
                onValueChange={([v]) => setAperture(v)}
                min={1.4}
                max={22}
                step={0.1}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Focus className="w-3 h-3" />
                {t('dof.focus')}: {focusDistance}m
              </Label>
              <Slider
                value={[focusDistance]}
                onValueChange={([v]) => setFocusDistance(v)}
                min={0.3}
                max={50}
                step={0.1}
              />
            </div>
          </div>

          {/* Quick Apertures */}
          <div className="flex flex-wrap gap-1">
            <span className="text-xs text-slate-500 mr-1">f/</span>
            {APERTURES.map(a => (
              <Button
                key={a}
                variant={Math.abs(aperture - a) < 0.1 ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setAperture(a)}
              >
                {a}
              </Button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-white/10"
              onClick={() => setFocusDistance(calculations.hyperfocal)}
            >
              <InfinityIcon className="w-4 h-4 mr-1" />
              {t('dof.focusAtHyperfocal')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFocalLength(50);
                setAperture(5.6);
                setFocusDistance(5);
              }}
              className="border-white/10"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">{t('dof.results')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('dof.nearLimit')}</p>
              <p className="font-mono text-lg text-red-400">{formatDistance(calculations.nearLimit)}</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('dof.farLimit')}</p>
              <p className="font-mono text-lg text-green-400">{formatDistance(calculations.farLimit)}</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('dof.totalDof')}</p>
              <p className="font-mono text-lg text-cyan-400">{formatDistance(calculations.dof)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('dof.hyperfocalDistance')}</p>
              <p className="font-mono text-sm text-purple-400">{formatDistance(calculations.hyperfocal)}</p>
              <p className="text-xs text-slate-600 mt-1">{t('dof.focusHereMaxSharpness')}</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('dof.dofDistribution')}</p>
              <p className="font-mono text-sm text-white">
                {formatDistance(calculations.dofFront)} {t('dof.front')} / {formatDistance(calculations.dofBehind)} {t('dof.back')}
              </p>
              <p className="text-xs text-slate-600 mt-1">{t('dof.oneThirdRule')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3 text-xs text-slate-400 space-y-1">
          <p>🎯 <strong>{t('dof.tipPortraitsLabel')}</strong> {t('dof.tipPortraitsDesc')}</p>
          <p>🏔️ <strong>{t('dof.tipLandscapesLabel')}</strong> {t('dof.tipLandscapesDesc')}</p>
          <p>♾️ <strong>{t('dof.tipHyperfocalLabel')}</strong> {t('dof.tipHyperfocalDesc')}</p>
          <p>📐 <strong>{t('dof.tipRuleLabel')}</strong> {t('dof.tipRuleDesc')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DOFCalculator;
